import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { ethers } from 'ethers';
import { handleGetChainInfo } from '@/utils/wallet';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from '@/providers/FeatureFlagProvider';
import { getL2ChainInfo } from '@/resources/wallet-service.resource';
import {
  BIP122ChainId,
  decryptMnemonic,
  initializeFoundationsCrypto,
} from '@nerdcoresdk/foundations-crypto';

import {
  fetchAndDecryptLegacyPrivateKey,
  fetchEncryptedMnemonic,
  prepareWallet,
} from '@/providers/helpers/web3Helper';

import { config } from '../config';

const Web3Context = createContext(null);

const includeBitcoinNetworks =
  process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
    ? [BIP122ChainId.Main]
    : [BIP122ChainId.Test3];

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};

export function Web3Provider({ children }) {
  const { fetcher, status, authStatuses } = useAuth();
  const { featureFlags, isLoading: featureFlagsLoading } = useFeatureFlags();

  const [error, setError] = useState(null);
  const [chainInfo, setChainInfo] = useState(null);
  const [l2ChainInfo, setL2ChainInfo] = useState(null);
  const [crypto, setCrypto] = useState(null);

  const useElevateSwapProvider =
    process.env.NEXT_PUBLIC_USE_ELEVATE_SWAP_PROVIDER === 'true' ||
    featureFlags?.useElevateSwapProvider === true;

  useEffect(() => {
    let isCurrent = true;

    initializeFoundationsCrypto({
      includeBitcoinNetworks,
      routes: {
        cryptoAssetsApi: config.core.walletServiceUrl,
        rpcProxy: '/api/crypto/rpc',
        evmTransactionHistoryProxy: '/api/crypto/evm-transactions',
        bitcoinNetworkProxy: '/api/crypto/bitcoin',
      },
    })
      .then((initializedCrypto) => {
        if (!isCurrent) return;
        setCrypto(initializedCrypto);
        setError(null);
      })
      .catch((err) => {
        if (!isCurrent) return;
        setError('Failed to initialize crypto');
        console.error('Failed to fetch Crypto Assets:', err);
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    if (authStatuses.SIGNED_IN !== status) return;

    let isCurrent = true;
    fetcher(getL2ChainInfo())
      .then((data) => {
        if (isCurrent) setL2ChainInfo(data);
      })
      .catch(() => console.error('Error fetching L2 chain data'));

    handleGetChainInfo(fetcher).then((loadedChainInfo) => {
      if (isCurrent && loadedChainInfo?.chainId) {
        setChainInfo(loadedChainInfo);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [status, authStatuses.SIGNED_IN, fetcher]);

  const provider = useMemo(() => {
    if (!chainInfo || !crypto) return null;
    try {
      const eip155ChainId = chainInfo?.chainId;
      const nativeCryptoAsset = crypto.assets.findNativeByEip155ChainId(eip155ChainId);

      if (!nativeCryptoAsset) {
        throw new Error(`Unsupported chain ID: ${eip155ChainId}`);
      }
      const provider = new ethers.providers.JsonRpcProvider(
        nativeCryptoAsset.blockchain.config.rpcUrl,
        Number(eip155ChainId)
      );

      return provider;
    } catch (err) {
      setError('Failed to initialize provider');
      console.error('Provider initialization error:', err);
      return null;
    }
  }, [chainInfo?.chainId, crypto]);

  const swapProvider = useMemo(() => {
    if (featureFlagsLoading || !crypto || !chainInfo?.chainId) {
      return null;
    }

    const eip155ChainId = Number(chainInfo.chainId);
    return crypto.createSwapProvider(
      useElevateSwapProvider
        ? { provider: 'elevate', eip155ChainId, apiUrl: config.elevate.swapApiUrl }
        : { provider: 'uniswap', eip155ChainId }
    );
  }, [chainInfo?.chainId, featureFlagsLoading, crypto, useElevateSwapProvider]);

  const isAllowedBuyToken = useCallback(
    (token) => {
      return swapProvider?.isAllowedBuyToken(token?.cryptoAsset) || false;
    },
    [swapProvider]
  );

  async function bridgePublicToPrivate({
    passcode,
    amountBN,
    sourceProvider,
    destinationProvider,
    sourceAssetLockContractAddress,
    tokenAddress,
    releaseFee,
    destinationAssetReleaseContractAddress,
    decimals,
  }) {
    try {
      const sourceWallet = await prepareWallet(fetcher, passcode, sourceProvider);
      const destinationWallet = await prepareWallet(
        fetcher,
        passcode,
        destinationProvider
      );
      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function approve(address spender, uint256 amount) external returns (bool)'],
        sourceWallet
      );
      const publicBridgeContract = new ethers.Contract(
        sourceAssetLockContractAddress,
        ['function lockTokens(uint256 amount, uint8 sourceChain) external'],
        sourceWallet
      );
      const privateBridgeContract = new ethers.Contract(
        destinationAssetReleaseContractAddress,
        ['function payReleaseFee() external payable'],
        destinationWallet
      );

      const releaseFeeBN = ethers.utils.parseUnits(releaseFee.toString(), decimals);

      await privateBridgeContract.payReleaseFee({ value: releaseFeeBN });

      const approveTx = await tokenContract.approve(
        sourceAssetLockContractAddress,
        amountBN
      );
      await approveTx.wait();

      const lockTx = await publicBridgeContract.lockTokens(amountBN, 0, {
        gasLimit: 500000n,
      });

      const receipt = await lockTx.wait();
      return receipt;
    } catch (err) {
      console.error('Transaction signing error:', err);
      throw err;
    }
  }

  async function bridgePrivateToPublic({
    passcode,
    amountBN,
    sourceProvider,
    destinationProvider,
    sourceAssetLockContractAddress,
    releaseFee,
    destinationAssetReleaseContractAddress,
  }) {
    try {
      const sourceWallet = await prepareWallet(fetcher, passcode, sourceProvider);
      const destinationWallet = await prepareWallet(
        fetcher,
        passcode,
        destinationProvider
      );

      const publicContract = new ethers.Contract(
        destinationAssetReleaseContractAddress,
        ['function payReleaseFee() external payable'],
        destinationWallet
      );

      // Let's try awaiting this and see if it takes to long
      await publicContract.payReleaseFee({ value: releaseFee, gasLimit: 300000n });

      const privateBridgeContract = new ethers.Contract(
        sourceAssetLockContractAddress,
        ['function lockTokens(uint8) external payable'],
        sourceWallet
      );

      const enumForChain = 1; // 1 for eth, 2 for binance This needs to be from somewhere, it is not the chain id will be added to bridgexconfig table
      const tx = await privateBridgeContract.lockTokens(enumForChain, {
        value: amountBN,
        gasLimit: 500000n,
      });

      const receipt = await tx.wait();

      return receipt;
    } catch (err) {
      console.error('Transaction signing error:', err);
      throw err;
    }
  }

  async function sendWeb3Transaction({ passcode, preview, core = true }) {
    if (!preview?.canSend || !preview.sendable || !preview.transaction) {
      throw new Error('A sendable CryptoAsset preview is required to send a transaction');
    }

    if (!core) {
      const privateKey = await fetchAndDecryptLegacyPrivateKey(
        preview.from,
        passcode,
        fetcher
      );

      return crypto.send(preview, { privateKey });
    } else {
      const encryptedData = await fetchEncryptedMnemonic(fetcher);
      let mnemonic = '';

      mnemonic = await decryptMnemonic(encryptedData, passcode);

      try {
        return await crypto.send(preview, { mnemonic });
      } finally {
        mnemonic = '';
      }
    }
  }

  const getSwapQuote = useCallback(
    async ({ fromCryptoAsset, toCryptoAsset, recipient, signer, amount }) => {
      try {
        if (!swapProvider) throw new Error('Swap provider not initialized');

        const quote = await swapProvider.getSwapQuote({
          fromCryptoAsset,
          toCryptoAsset,
          amount,
          recipient,
          signer,
        });

        if (!quote) throw new Error('Failed to get quote');

        console.info('Returning quote:', quote);
        return quote;
      } catch (err) {
        console.error('Failed to get swap quote:', err);
        throw err;
      }
    },
    [swapProvider]
  );

  const getMaxSwapAmount = useCallback(
    async ({ fromCryptoAsset, toCryptoAsset, recipient, signer, balance, precision }) => {
      if (!swapProvider) throw new Error('Swap provider not initialized');

      return swapProvider.getMaxSwapAmount({
        fromCryptoAsset,
        toCryptoAsset,
        amount: balance,
        recipient,
        signer,
        balance,
        precision,
      });
    },
    [swapProvider]
  );

  async function executeSwap({
    fromCryptoAsset,
    toCryptoAsset,
    amount,
    slippage = 0.3,
    wallet,
  }) {
    if (!wallet) throw new Error('Wallet not provided');
    if (!swapProvider) throw new Error('Swap provider not initialized');

    return swapProvider.executeSwap({
      fromCryptoAsset,
      toCryptoAsset,
      amount,
      slippage,
      wallet,
    });
  }

  async function getWalletFromPasscode(passcode) {
    const encryptedData = await fetchEncryptedMnemonic(fetcher);
    if (!encryptedData) throw new Error('No encrypted data available');

    try {
      const mnemonic = await decryptMnemonic(encryptedData, passcode);

      const wallet = ethers.Wallet.fromMnemonic(mnemonic);

      return wallet;
    } catch (err) {
      throw new Error('Invalid passcode: ', +err.message);
    }
  }

  const value = {
    chainInfo,
    crypto,
    error,
    executeSwap,
    getMaxSwapAmount,
    getSwapQuote,
    getWalletFromPasscode,
    isAllowedBuyToken,
    l2ChainInfo,
    provider,
    sendWeb3Transaction,
    bridgePrivateToPublic,
    bridgePublicToPrivate,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
}
